<script setup lang="ts">
import type { CourseRoleId } from '~/data/courseRoles'
import type { components } from '~/types/napis'

const props = defineProps({
  job: {
    type: Object as PropType<components['schemas']['JobResponseSchema']>,
    required: true
  },
  card: { type: Boolean, default: true }
})

const hasSaved = computed(() => Boolean(props.job.job_activity_part?.saved_record_id_slug))
const hasApplied = computed(() => Boolean(props.job.job_activity_part?.applied_record_id_slug))
const hasClosed = computed(() => props.job.status !== 'PUBLISHED')

const { status, data: user } = useAuth()
const { saveJob, unSaveJob } = useJob()

// use popup
const show = ref(false)
const { closePopup, showPopup } = usePopup()
const emits = defineEmits(['update:job'])

const handleShowPopup = () => showPopup(show)

const handleClosePopup = () => {
  closePopup(show)
  emits('update:job')
}

const clickApply = () => {
  if (status.value === 'authenticated') {
    handleShowPopup()
  } else {
    navigateTo({
      path: '/login',
      query: {
        next: 'apply_job',
        jobId: props.job.id_slug
      }
    })
  }
}

// BACKEND - applyJob
async function clickBookmark() {
  if (status.value !== 'authenticated') {
    return navigateTo({
      path: '/login',
      query: {
        next: 'save_job',
        jobId: props.job.id_slug
      }
    })
  }

  if (hasSaved.value) {
    if (!props.job.job_activity_part?.saved_record_id_slug) return
    await unSaveJob(props.job.job_activity_part?.saved_record_id_slug)
  } else {
    await saveJob(props.job.id_slug)
  }
  emits('update:job')
}

// View methods
const getClass = () => {
  let cls = 'job-item card-style '

  if (props.card) cls += 'job-card '

  return cls
}

const exclusivityText = computed(() => {
  if (props.job.exclusive_to_user_tags) {
    if (props.job.exclusive_to_user_tags.includes('Kyouth 2024 (A)'))
      return 'Exclusive to Kyouth 2024'
    if (props.job.exclusive_to_user_tags.includes('Kyouth 05-2024 (A)'))
      return 'Exclusive to Kyouth 2024 (A) Cohort 1'
    if (props.job.exclusive_to_user_tags.includes('Kyouth 06-2024 (A)'))
      return 'Exclusive to Kyouth 2024 (A) Cohort 2'
  }
  return ''
})
const hideApply = computed(() => {
  // Check if job is exclusive to some users
  if (props.job.exclusive_to_user_tags && props.job.exclusive_to_user_tags.length > 0) {
    if (status.value === 'unauthenticated') return false
    if (
      (user.value?.secret_part?.tags || []).some((tag) =>
        (props.job.exclusive_to_user_tags || []).includes(tag)
      )
    )
      return false
    return true
  }
  return false
})
</script>

<template>
  <div :class="getClass()">
    <img
      v-if="toCompanyIconUrl(props.job.company_part?.logo_part?.object_key)"
      :src="toCompanyIconUrl(props.job.company_part?.logo_part?.object_key)"
      :alt="props.job.company_part?.name || 'Company Logo'"
      class="job-item-logo"
    >
    <div v-else class="job-item-logo default-company-logo">
      <CommonIconJobsIconDefaultCompany />
    </div>

    <div class="job-item-title">
      <NuxtLink :to="getJobPageUrl(job.id_slug, job.title)">
        <h4>
          {{ job.title }}
        </h4>
      </NuxtLink>
      <h6 v-if="exclusivityText">{{ exclusivityText }}</h6>
    </div>

    <NuxtLink
      v-if="props.job.company_part"
      :to="`/employers/${props.job.company_part?.id_slug}`"
      class="job-item-employer"
    >
      <p>
        {{ props.job.company_part?.name }}
      </p>
    </NuxtLink>

    <div v-if="props.job.location" class="job-item-details">
      <CommonIconJobsIconLocationOutline />
      <span> {{ getReadableJobLocation(props.job.location) }}</span>
    </div>

    <div class="job-item-details-two">
      <div v-if="props.job.mode" class="job-item-details">
        <CommonIconJobsIconBriefcase />
        <span> {{ getReadableJobWorkMode(props.job.mode) }}</span>
      </div>

      <div v-if="props.job.work_mode" class="job-item-details">
        <CommonIconJobsIconBuilding />
        <span> {{ getReadableJobWorkMode(props.job.work_mode) }} </span>
      </div>

      <div v-if="props.job.posting_date" class="job-item-details">
        <CommonIconJobsIconTime />
        <span>{{ getReadableDayDifferenceFromToday(props.job.posting_date) }}</span>
      </div>
    </div>

    <div v-if="props.job.role" id="job-item-role" class="job-item-details">
      <CommonIconJobsIconSpanner />
      <span>{{ getReadableJobRole(props.job.role as CourseRoleId) }}</span>
    </div>

    <div v-if="!hideApply" class="job-item-btns">
      <CommonButton :outline="!hasSaved" :small="true" @click="clickBookmark">
        <CommonIconJobsIconBookmark />
      </CommonButton>

      <CommonButton v-if="!hasApplied" :small="true" :outline="true" @click="clickApply">
        Apply
      </CommonButton>
      <CommonButton v-else-if="hasClosed" disabled small> Closed </CommonButton>
      <CommonButton v-else small @click="clickApply"> Applied </CommonButton>
    </div>

    <PageJobsApplyJobScreen
      v-if="!hideApply && status === 'authenticated'"
      :show="show"
      :job="job"
      @show-popup="handleShowPopup"
      @close-popup="handleClosePopup"
    />
  </div>
</template>

<style scoped lang="scss">
$padding: 27px;
$btnsWidth: 160px;

.job-item {
  $imgSize: 55px;
  $leftPadding: calc($imgSize + 30px);
  $btnsWidth: 160px;
  $rightPadding: calc($btnsWidth + 30px);

  width: 100%;
  padding: $padding $rightPadding $padding $leftPadding;
  border-radius: 0;
  border-right: none;
  border-left: none;
  border-top: none;

  &-logo {
    height: $imgSize;
    width: $imgSize;
    position: absolute;
    top: $padding;
    left: $padding;
  }

  .default-company-logo {
    overflow: hidden;

    svg {
      height: 100%;
      width: 100%;
    }
  }

  &-title {
    margin: 3px 0 0;

    h4 {
      font-size: 1.1rem;
      line-height: 1.7rem;
      min-height: 0;
      margin: 0;
      margin-right: 12px;
      margin-bottom: 5px;
      display: inline-flex;
    }

    h6 {
      $size: 0.8rem;
      font-size: $size;
      line-height: $size;
      font-weight: 500;
      background-color: rgb(214, 255, 230);
      color: rgb(0, 152, 58);
      padding: 4px 8px;
      border-radius: 5px;
      display: inline-flex;
      margin-bottom: 10px;
    }
  }

  p {
    font-size: 1rem;
    margin: 0 0 7px;
    color: var(--textLight);
  }

  &-details {
    position: static;
    margin: 5px 0 0;
    display: flex;
    align-items: center;

    svg {
      $size: 14px;
      fill: var(--textMedium);
      height: $size;
      width: $size;
      margin-right: 15px;
    }

    span {
      font-size: 0.93rem;
      color: var(--black);

      b {
        padding: 0 7px;
      }
    }

    &-style {
      display: inline-block;
    }

    &-two {
      display: flex;
      column-gap: 50px;
      flex-wrap: wrap;
    }
  }

  &-featured {
    display: flex;
    margin-top: 4px;
    align-items: center;

    span {
      font-size: 0.85rem;
      margin-top: 2px;
    }

    svg {
      $size: 13px;
      height: $size;
      width: $size;
      fill: rgb(189, 189, 189);
      margin-right: 10px;
    }
  }

  &-btns {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: $btnsWidth;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding-top: 30px;
    gap: 10px;

    a {
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        $size: 16px;
        height: $size;
        width: $size;
      }
    }

    .btn-s:first-of-type {
      padding-right: 13px;
      padding-left: 13px;
    }
  }
}

.job-card {
  padding-left: 110px;
  padding-right: calc($btnsWidth + $padding);
  border-radius: 20px;
  margin-bottom: 15px;
  border: 1px solid var(--borderMedium);

  img {
    left: $padding;
  }

  .job-item-btns {
    right: $padding;
  }
}

@media (max-width: 700px) {
  .job-card,
  .job-item {
    border: 1px solid gainsboro;
    border-radius: 15px;
    padding: 20px;
    display: block;
    margin-bottom: 22px;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.05);

    $size: 50px;

    img,
    &-logo {
      height: $size;
      position: relative;
      top: 0;
      left: 0;
      border-radius: 10px;
      border: none;
      width: auto;
      max-width: 100px;
      object-position: center left;
    }

    .default-company-logo {
      width: $size;
    }

    &-title {
      margin: 13px 0 5px;

      h4 {
        height: auto;
        min-height: 0;
        max-height: none;
        font-size: 1.05rem;
        line-height: 1.55rem;
        overflow: visible;
      }
    }

    p {
      margin-bottom: 15px;
    }

    &-details {
      span {
        font-size: 0.9rem;
      }

      svg {
        flex-shrink: 0;
      }

      &-two {
        display: flex;
        flex-wrap: wrap;
        gap: 0;
        column-gap: 50px;
      }
    }

    #job-item-role {
      display: none;
    }

    &-btns {
      $btnSize: 35px;
      padding: 0;
      height: $btnSize;
      width: $btnSize;
      top: 20px;

      .btn-s {
        height: $btnSize;
        width: $btnSize;
        padding: 0;

        svg {
          $svgSize: 16px;

          height: $svgSize;
          width: $svgSize;
          flex-shrink: 0;
        }
      }

      .btn-s:last-of-type {
        display: none;
      }
    }
  }

  .job-card {
    img {
      left: 0;
    }

    .job-item-btns {
      right: 20px;
    }
  }
}

@media (hover: hover) {
  .job-item {
    &-employer:hover {
      p {
        color: var(--purple);
        text-decoration: underline;
      }
    }

    &-btns {
      .btn-s:first-of-type:hover {
        background: var(--purple);

        svg {
          fill: white;
        }
      }
    }
  }
}
</style>
